import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import { faX } from '@fortawesome/free-solid-svg-icons';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, BoxProps, InputAdornment, Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { ClearIcon } from '@mui/x-date-pickers';
import { forwardRef, ReactNode, useState } from 'react';

interface TextFieldTsoPqProps extends Omit<TextFieldProps, 'label'> {
  mandatory?: boolean;
  label?: string;
  supportingText?: string;
  tooltip?: boolean;
  characterLimit?: number;
  iconLeft?: ReactNode;
  clearInput?: boolean;
  customwidth?: string;
  multiline?: boolean;
  whiteBackground?: boolean;
  handleChange: (value: string) => void;
  resetSearch?: React.Dispatch<React.SetStateAction<string[]>>;
}

interface StyledSupportingTextProps extends TypographyProps {
  error?: boolean;
}

type SyledTextFieldProps = TextFieldProps & {
  customwidth?: string;
  multiline: boolean;
  whiteBackground?: boolean;
};

type StyledInputContainer = BoxProps & {
  customwidth?: string;
};

const StyledInputContainer = styled(Box)<StyledInputContainer>(({ customwidth }) => {
  return {
    width: customwidth ?? '11.875rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  };
});

const StyledTooltip = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 0 0 0.25rem',
});

const StyledLabel = styled(Typography)({
  display: 'flex',
  color: tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
  fontSize: tsoPqTheme.typography.fontsize_body_body_small,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '1rem',
});

const StyledTooltipIcon = styled(HelpOutlineIcon)({
  fontSize: tsoPqTheme.typography.fontsize_body_body_small,
  color: tsoPqTheme.palette.customTextColor.color_font_ft_second_text,

  '&:hover': { cursor: 'pointer' },
});

const StyledMandatory = styled('span')({
  color: tsoPqTheme.palette.customError.color_signal_error_error,
  fontFamily: tsoPqTheme.typography.openSans,
  fontSize: tsoPqTheme.typography.fontsize_body_body_small,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '0.875rem',
});

const StyledDescription = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '0.625rem',
});

const StyledSupportingText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'error',
})<StyledSupportingTextProps>(({ error }) => {
  return {
    color: error
      ? tsoPqTheme.palette.customError.color_signal_error_error
      : tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '0.875rem',
    padding: '0 0 0 0.5rem',
  };
});

const StyledCharacterLimit = styled(Typography)({
  color: tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
  fontFamily: tsoPqTheme.typography.openSans,
  fontSize: tsoPqTheme.typography.fontsize_body_body_small,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '0.875rem',
});

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !['customwidth', 'whiteBackground'].includes(prop as string),
})<SyledTextFieldProps>(({ multiline, customwidth, whiteBackground }) => {
  return {
    '& .MuiInputBase-root': {
      height: multiline ? 'auto' : '2rem',
      width: customwidth || '11.875rem',
      padding: '0.5rem 0 0.5rem 0.25rem',
      backgroundColor: tsoPqTheme.palette.customTextColor.color_font_ft_white,
      '&:hover': {
        background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled_lt,
      },

      '&.Mui-focused': {
        background: whiteBackground
          ? tsoPqTheme.palette.customTextColor.color_font_ft_white
          : tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_lt,
      },

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_hover,
      },

      '& .MuiInputAdornment-root': {
        margin: '0',
      },

      '& .MuiInputBase-input': {
        padding: '0.25rem 0 0.25rem 0.5rem',
        color: tsoPqTheme.palette.customTextColor.color_font_ft_second_text,
        fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
        fontFamily: tsoPqTheme.typography.openSans,
        lineHeight: '1.3125rem',
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_hover}`,
      },

      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.customError.color_signal_error_error}`,
      },

      '&.Mui-disabled': {
        backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_disabled,

        '& .MuiInputBase-input': {
          WebkitTextFillColor: tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled,
        },
      },
    },
  };
});

const StyledClearIcon = styled(ClearIcon)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent,
    padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_4,
    height: '1.125rem',

    '&:hover': {
      cursor: 'pointer',
    },
  };
});

const TextFieldTsoPq = forwardRef<HTMLInputElement, TextFieldTsoPqProps>(
  (
    {
      mandatory,
      label,
      supportingText,
      characterLimit,
      tooltip = false,
      iconLeft,
      clearInput = false,
      multiline = false,
      customwidth,
      disabled = false,
      handleChange,
      resetSearch,
      ...props
    },
    ref
  ) => {
    const [remainingCharacters, setRemainingCharacters] = useState<number | undefined>(characterLimit);

    const updateCharacterLimit = (value: string) => {
      if (characterLimit) setRemainingCharacters(characterLimit - value.length);
    };

    return (
      <StyledInputContainer customwidth={customwidth}>
        <StyledTooltip>
          <StyledLabel>
            {label}
            {mandatory && <StyledMandatory>*</StyledMandatory>}
          </StyledLabel>
          {tooltip && <StyledTooltipIcon />}
        </StyledTooltip>
        <StyledTextField
          {...props}
          disabled={disabled}
          multiline={multiline}
          customwidth={customwidth}
          inputRef={ref}
          onChange={(event) => {
            handleChange(event.target.value);
            updateCharacterLimit(event.target.value);
          }}
          slotProps={{
            htmlInput: { maxLength: characterLimit },
            input: {
              startAdornment: <InputAdornment position="start">{iconLeft}</InputAdornment>,
              endAdornment: clearInput && (
                <InputAdornment position="end">
                  <IconButtonTsoPq
                    sizeVariant={'s'}
                    icon={faX}
                    buttonType="transparentPrimaryNoEffect"
                    onClick={() => {
                      handleChange('');
                      if (resetSearch) resetSearch([]);
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
        <StyledDescription>
          <StyledSupportingText error={props.error}>{supportingText}</StyledSupportingText>
          {(!disabled || characterLimit) && <StyledCharacterLimit>{remainingCharacters}</StyledCharacterLimit>}
        </StyledDescription>
      </StyledInputContainer>
    );
  }
);

export default TextFieldTsoPq;
